import { createAri, type Ari } from '@atlassian/jira-platform-ari';
import type { QueryResult } from '@atlassian/jira-relay-apollo-migration-utils/src/services/use-query/index.tsx';
import type {
	getEpicsRelayCycleTimeReportQuery$variables,
	getEpicsRelayCycleTimeReportQuery$data,
} from '@atlassian/jira-relay/src/__generated__/getEpicsRelayCycleTimeReportQuery.graphql';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { ServiceQueryResult } from '@atlassian/jira-software-deployments-service-query-types/src/services/types.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { Epic } from '../../common/types';
import { getResourceId } from '../../common/utils/ari';
import { useProjectId } from '../get-project';

export const useProjectAri = (): Ari | null => {
	const cloudId = useCloudId();
	const projectId = useProjectId();

	if (projectId === null) {
		return null;
	}

	return createAri({
		resourceOwner: 'jira',
		cloudId,
		resourceType: 'project',
		resourceId: projectId,
	});
};

const transformEpic = (
	epic:
		| {
				readonly id: string;
				readonly summary: string;
		  }
		| null
		| undefined,
): Epic => {
	if (epic == null) {
		throw Error('epic cannot be null');
	}
	return {
		id: toIssueId(getResourceId(epic.id)),
		summary: epic.summary ?? '',
	};
};

export const transformQueryResult = ({
	loading,
	error,
	data,
}: QueryResult<
	getEpicsRelayCycleTimeReportQuery$variables,
	getEpicsRelayCycleTimeReportQuery$data
>): ServiceQueryResult<Epic[]> => {
	if (loading) {
		return {
			loading: true,
		};
	}
	if (error) {
		return {
			error,
			loading: false,
		};
	}
	// This is possible due to the `skip` condition on the query
	if (data == null) {
		return {
			loading: false,
		};
	}
	if (data.jiraReleases?.epics?.nodes == null) {
		return {
			loading: false,
			error: Error('Epics not found'),
		};
	}

	const transformationResult = data.jiraReleases.epics.nodes.map(transformEpic);

	return transformationResult instanceof Error
		? {
				error: transformationResult,
				loading: false,
			}
		: {
				data: transformationResult,
				loading: false,
			};
};
